import {Card} from 'platform/components';
import {Box, Show, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {isEmpty, isNil} from 'ramda';

import type {AbcXyzAnalysisDetailResponse} from '@dms/api/reporting';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, type RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useAbcXyzChartColors} from '../hooks/useAbcXyzChartColors';
import type {AbcXyzLegendItem} from '../types/AbcXyzLegendItem';
import {AbcXyzChartLegend} from './AbcXyzChartLegend';

const Y_AXIS_WIDTH = 70;
const VALUE_AXIS_ID = 'value';
const SHARE_AXIS_ID = 'share';
const MAX_SHARE_PERCENT = 100;
const VALUE_DECIMALS = 2;
/** Length of the `YYYY-MM` prefix of an ISO date, which is all the month axis needs. */
const MONTH_LABEL_LENGTH = 7;

interface AbcXyzValueContributionChartProps extends RequiredTestIdProps {
  detail: AbcXyzAnalysisDetailResponse;
}

/**
 * Monthly net consumption value as bars and the cumulative share of the window's total as a
 * curve. The API sends the twelve months of the evaluation window; the chart plots them as
 * received. Until the API returns the history, the card says so instead of showing an empty chart.
 */
export function AbcXyzValueContributionChart(props: AbcXyzValueContributionChartProps) {
  const formatNumber = useFormatNumber();
  const colors = useAbcXyzChartColors();

  const history = props.detail.abcValueHistory ?? [];
  const data = history.map((month) => ({
    month: month.month?.slice(0, MONTH_LABEL_LENGTH) ?? EMPTY_PLACEHOLDER,
    netConsumptionValue: month.netConsumptionValue,
    cumulativeSharePercent: month.cumulativeSharePercent,
  }));

  const legendItems: AbcXyzLegendItem[] = [
    {
      id: 'monthlyValue',
      label: i18n.t('entity.warehouse.reporting.monthlyValue'),
      color: colors.primary,
      shape: 'bar',
    },
    {
      id: 'cumulativeShare',
      label: i18n.t('entity.warehouse.reporting.cumulativeShare'),
      color: colors.accent,
      shape: 'line',
    },
  ];

  return (
    <Card isFullHeight data-testid={props['data-testid']}>
      <VStack spacing={2} height="100%" minHeight={0}>
        <Text size="small" alternative>
          {i18n.t('entity.warehouse.reporting.valueContributionChartTitle')}
        </Text>
        {isEmpty(data) ? (
          <Text color="secondary" data-testid={suffixTestId('unavailable', props)}>
            {i18n.t('entity.warehouse.reporting.valueContributionUnavailable')}
          </Text>
        ) : (
          <VStack spacing={2} grow={1} basis={0} minHeight={0}>
            <AbcXyzChartLegend items={legendItems} data-testid={suffixTestId('legend', props)} />
            <Box width="100%" flex={1} minHeight={0}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} />
                  <YAxis
                    yAxisId={VALUE_AXIS_ID}
                    width={Y_AXIS_WIDTH}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value: number) => formatNumber(value, 0) ?? ''}
                  />
                  <YAxis
                    yAxisId={SHARE_AXIS_ID}
                    orientation="right"
                    domain={[0, MAX_SHARE_PERCENT]}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value: number) => `${value} %`}
                  />
                  <Tooltip
                    formatter={(value: number) =>
                      isNil(value) ? EMPTY_PLACEHOLDER : (formatNumber(value, VALUE_DECIMALS) ?? '')
                    }
                  />
                  <Bar
                    yAxisId={VALUE_AXIS_ID}
                    dataKey="netConsumptionValue"
                    name={i18n.t('entity.warehouse.reporting.monthlyValue')}
                    fill={colors.primary}
                  />
                  <Line
                    yAxisId={SHARE_AXIS_ID}
                    type="monotone"
                    dataKey="cumulativeSharePercent"
                    name={i18n.t('entity.warehouse.reporting.cumulativeShare')}
                    stroke={colors.accent}
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
            <Show when={props.detail.abcValueHistoryMatchesSnapshot === false}>
              <Text size="small" color="secondary" data-testid={suffixTestId('mismatch', props)}>
                {i18n.t('entity.warehouse.reporting.valueHistoryDiffersFromSnapshot')}
              </Text>
            </Show>
          </VStack>
        )}
      </VStack>
    </Card>
  );
}
